import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { setPageIndex } from '../../redux/slices/paginationSlice';
import SearchInput from './SearchInput';
import { Pagination } from '../../modules/clientModule/components/Common/Table/types';
import PaginationMobile from '../../pages/Responsive/PaginationMobile';

const TableHeaders = ({
  headers,
  rows,
  isLoading,
  pristine,
  children,
  onSort = undefined,
  sortKey = '',
  sortDirection = 'asc',
}) => {
  const dispatch = useDispatch();
  const ref = useRef(null);
  const [isScrollVisible, setIsScrollVisible] = useState<boolean>(false);
  useEffect(() => {
    const checkScrollbar = () => {
      if (ref.current) {
        const hasVerticalScrollbar = ref.current.scrollHeight > ref.current.clientHeight;
        setIsScrollVisible(hasVerticalScrollbar);
      }
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);
    return () => {
      dispatch(setPageIndex(0));
      window.removeEventListener('resize', checkScrollbar);
    };
  }, []);

  return (
    <>
      <TableCard>
        <HeadersRow count={headers.length}>
          {headers.map((header, index) => (
            <HeaderCell key={index} onClick={() => onSort?.(header.name)} isSortable={!!header.name}>
              <span>{header.label}</span>
              {header.name && (
                <SortIcons>
                  <span style={{ opacity: sortKey === header.name && sortDirection === 'asc' ? 1 : 0.3 }}>▲</span>
                  <span style={{ opacity: sortKey === header.name && sortDirection === 'desc' ? 1 : 0.3 }}>▼</span>
                </SortIcons>
              )}
            </HeaderCell>
          ))}
        </HeadersRow>
        <SearchRow count={headers.length} isScrollVisible={isScrollVisible}>
          {headers.map((search, index) => {
            if (search.name) {
              return <SearchInput key={index} name={search.name} />;
            }
            return null;
          })}
        </SearchRow>
        {!pristine && rows.length === 0 && <NoRecords>No matching records found</NoRecords>}
        <RowScroller ref={ref}>{children}</RowScroller>
      </TableCard>
      {rows.length > 0 && (
        <Pagination>
          <PaginationMobile showEverywhere />
        </Pagination>
      )}
    </>
  );
};

export default TableHeaders;

const FONT_STACK = `'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif`;

export const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  font-family: ${FONT_STACK};
`;

const TableCard = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  overflow: hidden;
  font-family: ${FONT_STACK};
`;

const HeadersRow = styled.div<{ count: number }>`
  display: grid;
  grid-template-columns: ${({ count }) => `repeat(${count}, minmax(0, 1fr))`};
  gap: 0;
  background: var(--table-row-header);
  border-bottom: 1px solid var(--border);
`;

const SortIcons = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 9px;
  line-height: 9px;
  margin-left: 6px;
`;

const SearchRow = styled.div<{ count: number; isScrollVisible: boolean }>`
  display: grid;
  grid-template-columns: ${({ count }) => `repeat(${count}, minmax(0, 1fr))`};
  gap: 0;
  background: var(--table-row-header);
  border-bottom: 1px solid var(--border);
  padding-right: ${({ isScrollVisible }) => (isScrollVisible ? '12px' : '0')};
`;

export const SingleRow = styled.div<{ count: number; isEmergency: boolean }>`
  display: grid;
  grid-template-columns: ${({ count }) => `repeat(${count}, minmax(0, 1fr))`};
  gap: 0;
  padding: 0;
  background: ${({ isEmergency }) => (isEmergency ? 'var(--danger-50, #FEF2F2)' : 'transparent')};
  transition: background-color 0.15s ease;

  &:hover {
    background: ${({ isEmergency }) => (isEmergency ? 'var(--danger-100, #FEE2E2)' : 'var(--table-row-hover)')};
  }
`;

const HeaderCell = styled.div<{ isSortable?: boolean }>`
  padding: 12px 16px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--neutral-500);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  min-width: 0;
  cursor: ${({ isSortable }) => (isSortable ? 'pointer' : 'default')};
  user-select: none;

  > span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const DataCell = styled.div`
  overflow: hidden;
  padding: 14px 16px;
  font-size: 0.875rem;
  color: var(--mainText);
  font-family: ${FONT_STACK};
  display: flex;
  align-items: center;
  min-width: 0;
  word-break: break-word;
`;

export const Icons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  padding: 14px 16px;
`;

const NoRecords = styled.div`
  color: var(--neutral-500);
  width: 100%;
  text-align: center;
  padding: 32px 16px;
  font-size: 0.875rem;
`;

const RowScroller = styled.div`
  max-height: 78vh;
  overflow-y: auto;
`;

export const RowWrapper = styled.div<{ index }>`
  color: var(--mainText);
  background: ${({ index }) => (index % 2 === 1 ? 'var(--table-row-alt)' : 'var(--table-row-base)')};
  border-bottom: 1px solid var(--border);
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: var(--table-row-hover);
  }
`;
